import { getMediaType } from '@athena-lib/asset-utils';
import type { MaybeResponsive, Responsive } from '@athena-lib/responsive-utils';
import { objectEntries } from '@athena-lib/object-utils';
import clsx from 'clsx';
import { getOptimizedImageUrl } from '@athena-lib/Image';
import {
  backgrounds,
  responsiveBackgrounds,
  type PageSectionBackground,
} from '@athena-lib/container-context.svelte';
import type { ImageContext } from '@athena-lib/Image/types';
import {
  bottomPaddings,
  responsiveBottomPaddings,
  responsiveTopPaddings,
  topPaddings,
  type Spacing,
} from '@athena-lib/spacing';
import { deviceNames, type DeviceName } from '@athena-lib/device';
import type { PageSectionYSpace } from './types';

export const getBackgroundClass = (
  backgroundColor: MaybeResponsive<PageSectionBackground> | undefined,
  backgroundMediaUrl: MaybeResponsive<string> | undefined,
  fullWidthBackgroundColor: MaybeResponsive<boolean>,
  isOutermost: boolean,
) => {
  const classes = [];
  if (backgroundMediaUrl) {
    return undefined;
  }
  if (fullWidthBackgroundColor === false && isOutermost) {
    return undefined;
  }
  if (typeof backgroundColor === 'object') {
    classes.push(
      ...objectEntries(backgroundColor)
        .filter(
          ([deviceName, _]) =>
            // We filter out all backgrounds for the outermost element where we don't have a full width background
            !(isOutermost && isFalseIn(deviceName, fullWidthBackgroundColor)),
        )
        .map(
          ([deviceName, pageSectionBackground]) =>
            responsiveBackgrounds[deviceName][pageSectionBackground],
        ),
    );
  } else if (backgroundColor && backgroundColor in backgrounds) {
    classes.push(
      getNonResponsiveBackgroundColor(isOutermost, fullWidthBackgroundColor, backgroundColor),
    );
  }
  return clsx(classes);
};

const isFalseIn = (deviceName: DeviceName, value: MaybeResponsive<boolean>) =>
  value === false || (value as Responsive<boolean>)[deviceName] === false;

const getNonResponsiveBackgroundColor = (
  isOutermost: boolean,
  fullWidthBackgroundColor: boolean | Responsive<boolean>,
  backgroundColor: PageSectionBackground,
) => {
  if (!isOutermost || typeof fullWidthBackgroundColor !== 'object') {
    return backgrounds[backgroundColor].join(' ');
  }

  return clsx(
    objectEntries(fullWidthBackgroundColor).map(([deviceName, value]) => {
      if (value) {
        return responsiveBackgrounds[deviceName][backgroundColor];
      } else {
        // On the outermost element we need to set the background color to transparent
        // if it's not full width
        switch (deviceName) {
          case 'sm':
            return 'bg-transparent';
          case 'md':
            return 'md:bg-transparent';
          case 'lg':
            return 'lg:bg-transparent';
        }
      }
    }),
  );
};

export const getBackgroundImageCssVariable = (
  backgroundMediaUrl: MaybeResponsive<string> | undefined,
  imageContext: ImageContext | undefined,
) => {
  const mobileStyle = getImageCssVariable(
    getBackgroundImage(backgroundMediaUrl, 'sm'),
    imageContext,
    'sm',
  );
  const tabletStyle = getImageCssVariable(
    getBackgroundImage(backgroundMediaUrl, 'md'),
    imageContext,
    'md',
  );

  const desktopStyle = getImageCssVariable(
    getBackgroundImage(backgroundMediaUrl, 'md'),
    imageContext,
    'lg',
  );

  return mobileStyle || tabletStyle || desktopStyle
    ? mobileStyle + tabletStyle + desktopStyle
    : undefined;
};

const getBackgroundImage = (
  backgroundMediaUrl: MaybeResponsive<string> | undefined,
  deviceName: DeviceName,
) =>
  backgroundMediaUrl
    ? typeof backgroundMediaUrl === 'string'
      ? backgroundMediaUrl
      : backgroundMediaUrl[deviceName]
    : undefined;

const getImageCssVariable = (
  image: string | undefined,
  imageContext: ImageContext | undefined,
  deviceName: DeviceName,
) =>
  image && getMediaType(image).image
    ? '--' +
      (deviceName !== 'sm' ? deviceName + '-' : '') +
      'background-image-url: url("' +
      getOptimizedImageUrl(image, imageContext) +
      '");'
    : '';

const pageSectionPaddingsToTailwindPaddings: Record<PageSectionYSpace, Spacing> = {
  none: 'none',
  gutter: 'gutter',
  small: 'gutter',
  medium: 'large',
  large: 'xlarge',
  xlarge: 'xxlarge',
};

export const getPageSectionYPadding = (
  ySpace:
    | MaybeResponsive<PageSectionYSpace>
    | {
        top: MaybeResponsive<PageSectionYSpace>;
        bottom: MaybeResponsive<PageSectionYSpace>;
      },
) => {
  if (ySpace === 'none') {
    return '';
  }

  if (typeof ySpace === 'string' && ySpace) {
    const tailwindPadding = pageSectionPaddingsToTailwindPaddings[ySpace];
    return clsx(topPaddings[tailwindPadding], bottomPaddings[tailwindPadding]);
  }

  if (isResponsiveObject(ySpace)) {
    return getResponsivePaddings(ySpace, 'both');
  }

  const classes = [];
  if (typeof ySpace.top === 'string' && ySpace.top) {
    const tailwindPadding = pageSectionPaddingsToTailwindPaddings[ySpace.top];
    classes.push(topPaddings[tailwindPadding]);
  } else if (isResponsiveObject(ySpace.top)) {
    classes.push(getResponsivePaddings(ySpace.top, 'top'));
  }

  if (typeof ySpace.bottom === 'string' && ySpace.bottom) {
    const tailwindPadding = pageSectionPaddingsToTailwindPaddings[ySpace.bottom];
    classes.push(bottomPaddings[tailwindPadding]);
  } else if (isResponsiveObject(ySpace.bottom)) {
    classes.push(getResponsivePaddings(ySpace.bottom, 'bottom'));
  }

  return clsx(classes);
};

// Typically you always want border-box as box-sizing but we use box-sizing here
// easily switch where the X spacing is on the page section. On a non-full width
// background image we want the X spacing to be inside the border
// so we use box-content. On a full width background image we want the X spacing
// to be outside the border of the image so we use box-border.
export const getBoxSizingClass = (
  backgroundImageStyle: string | undefined,
  fullWidthBackgroundMedia: MaybeResponsive<boolean>,
  fullWidthBackgroundColor: MaybeResponsive<boolean>,
) => {
  const fullWidthBackground = backgroundImageStyle
    ? fullWidthBackgroundMedia
    : fullWidthBackgroundColor;

  const classes = [];
  if (typeof fullWidthBackground === 'boolean') {
    classes.push(!fullWidthBackgroundMedia ? 'box-border' : 'box-content');
  } else {
    classes.push(!fullWidthBackground.sm ? 'box-border' : 'box-content');
    classes.push(!fullWidthBackground.md ? 'md:box-border' : 'md:box-content');
    classes.push(!fullWidthBackground.lg ? 'lg:box-border' : 'lg:box-content');
  }
  return clsx(classes);
};

const getResponsivePaddings = (
  ySpace: Responsive<PageSectionYSpace>,
  location: 'top' | 'bottom' | 'both',
) =>
  objectEntries(ySpace)
    .map(([deviceName, space]) => {
      const tailwindPadding = pageSectionPaddingsToTailwindPaddings[space];
      return clsx(
        (location === 'top' || location === 'both') &&
          (deviceName === 'sm'
            ? topPaddings[tailwindPadding]
            : responsiveTopPaddings[deviceName + '_' + tailwindPadding]),
        (location === 'bottom' || location === 'both') &&
          (deviceName === 'sm'
            ? bottomPaddings[tailwindPadding]
            : responsiveBottomPaddings[deviceName + '_' + tailwindPadding]),
      );
    })
    .join(' ');

const isResponsiveObject = (obj: object): obj is Responsive<PageSectionYSpace> => {
  const keys = Object.keys(obj);
  return keys.every((key) => deviceNames.includes(key as DeviceName));
};
